import { useEffect, useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Loader2, User as UserIcon, Camera } from 'lucide-react';
import { SaveButton } from '../../components/ui/SaveButton';
import { errorMessage, reportError } from '../../lib/errors';
import toast from 'react-hot-toast';

/**
 * The avatars bucket is private, so profiles.avatar_url holds the object path
 * (`<user_id>/avatar.<ext>`) and the image is shown through a short-lived
 * signed URL. A full URL — a Google sign-in photo, say — is used as it is.
 */
const resolveAvatarSrc = async (avatarUrl: string): Promise<string> => {
    if (!avatarUrl) return '';
    if (/^https?:\/\//i.test(avatarUrl)) return avatarUrl;
    const { data, error } = await supabase.storage
        .from('avatars')
        .createSignedUrl(avatarUrl, 60 * 60);
    if (error) {
        reportError(error, 'identity-section.avatars');
        return '';
    }
    return data.signedUrl;
};

export const IdentitySection = () => {
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [profile, setProfile] = useState({
        full_name: '',
        email: '',
        avatar_url: ''
    });
    const [avatarSrc, setAvatarSrc] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const fetchProfile = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            // Fetch from profiles table
            const { data, error: fetchError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .single();
            if (fetchError) reportError(fetchError, 'identity-section.profiles');

            if (data) {
                setProfile({
                    full_name: data.full_name || '',
                    email: user.email || '',
                    avatar_url: data.avatar_url || ''
                });
                setAvatarSrc(await resolveAvatarSrc(data.avatar_url || ''));
            } else {
                setProfile(prev => ({ ...prev, email: user.email || '' }));
            }
            setLoading(false);
        };

        fetchProfile();
    }, []);

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file size (2MB max)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Image must be less than 2MB');
            return;
        }

        setUploading(true);

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            // Generate unique filename
            const fileExt = file.name.split('.').pop();
            const fileName = `${user.id}/avatar.${fileExt}`;

            // Upload to Supabase Storage
            const { error: uploadError } = await supabase.storage
                .from('avatars')
                .upload(fileName, file, { upsert: true });

            if (uploadError) throw uploadError;

            // Store the path, not a URL: the bucket is private.
            const { error: updateError } = await supabase
                .from('profiles')
                .update({ avatar_url: fileName })
                .eq('id', user.id);

            if (updateError) throw updateError;

            setProfile(prev => ({ ...prev, avatar_url: fileName }));
            // A fresh signed URL also defeats the cached copy of the old photo.
            setAvatarSrc(await resolveAvatarSrc(fileName));

        } catch (error) {
            console.error('Avatar upload error:', error);
            toast.error('Failed to upload avatar: ' + errorMessage(error));
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { error } = await supabase
            .from('profiles')
            .update({
                full_name: profile.full_name,
                avatar_url: profile.avatar_url
            })
            .eq('id', user.id);

        if (error) {
            toast.error('Error updating profile');
            throw error;
        }
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-fg-4" /></div>;

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-fg">Personal Details</h3>
                <p className="text-sm text-fg-2">Update your public identity</p>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-start">

                {/* Avatar */}
                <div className="flex flex-col items-center gap-4">
                    <div className="relative group">
                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-panel ring-1 ring-line bg-raised flex items-center justify-center">
                            {avatarSrc ? (
                                <img src={avatarSrc} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                                <UserIcon className="w-12 h-12 text-fg-2 opacity-50" />
                            )}
                        </div>
                        {/* Upload overlay. The scrim is the hover affordance on a
                            desktop; on touch it would just black out the avatar, so
                            the camera badge below carries the message instead. */}
                        <div
                            onClick={handleAvatarClick}
                            className="absolute inset-0 bg-scrim/50 rounded-full flex items-center justify-center fd-hover-reveal cursor-pointer"
                        >
                            {uploading ? (
                                <Loader2 className="w-8 h-8 text-fg/80 animate-spin" />
                            ) : (
                                <Camera className="w-8 h-8 text-fg/80" />
                            )}
                        </div>
                        {/* Persistent "you can change this" badge — the only cue a
                            phone gets, since there is no hover to reveal the scrim. */}
                        <button
                            type="button"
                            onClick={handleAvatarClick}
                            aria-label="Change profile photo"
                            className="absolute bottom-0 right-0 w-11 h-11 flex items-center justify-center rounded-full fd-btn-primary border-4 border-panel transition-transform active:scale-95 lg:hidden"
                        >
                            <Camera className="w-4 h-4" />
                        </button>
                        {/* Hidden file input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleFileChange}
                            className="hidden"
                        />
                    </div>
                    <div className="text-xs text-fg-2 text-center max-w-[150px]">
                        Click to upload a new photo
                    </div>
                </div>

                {/* Form */}
                <div className="flex-1 space-y-6 w-full">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-fg-2">Full Name</label>
                        <input
                            type="text"
                            value={profile.full_name}
                            onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                            className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                            placeholder="John Doe"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-fg-2">Email Address</label>
                        <input
                            type="email"
                            value={profile.email}
                            disabled
                            className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg-2 opacity-70 cursor-not-allowed"
                        />
                        <p className="text-xs text-fg-2">To change your email, please use the Security tab.</p>
                    </div>

                    <div className="pt-4">
                        <SaveButton onClick={handleSave} />
                    </div>
                </div>
            </div>
        </div>
    );
};
